import { useState, useEffect, useCallback } from 'react';
import {
  HiGlobeAlt,
  HiShoppingBag,
  HiCurrencyDollar,
  HiCube,
  HiUsers,
  HiRefresh,
  HiExclamationCircle,
  HiChevronDown,
  HiClock,
  HiUser,
} from 'react-icons/hi';
import { useAlert } from '../contexts/AlertContext';

const API_BASE = '/api/admin';

// Mondoway stores money as Prisma Decimal, which serialises to a JSON *string*
// on the order items ("2.8000"). Only the order-level totals arrive as numbers,
// so every amount goes through here before formatting.
const money = (value) => Number(value || 0).toFixed(2);

export default function MondowayOrders() {
  const showAlert = useAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchOrders = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      // Mondoway's own retail orders, proxied through the server so the
      // MONDOWAY_* credentials never reach the browser.
      const res = await fetch(`${API_BASE}/mondoway/orders`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `فشل في تحميل الطلبات (${res.status})`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Mondoway answers { recordsFiltered, recordsTotal, items[] } where each item
  // is a RetailOrder with its Items[], ShippingAddress and users. Nothing is
  // reshaped server-side, so the totals are computed here.
  const orders = data?.items || [];
  const summary = {
    orderCount: data?.recordsTotal ?? orders.length,
    totalRevenue:
      Math.round(orders.reduce((sum, order) => sum + Number(order.total || 0), 0) * 100) / 100,
    totalItems: orders.reduce(
      (sum, order) => sum + (order.Items || []).reduce((n, item) => n + item.quantity, 0),
      0
    ),
    customerCount: new Set(orders.map((order) => order.customerId).filter(Boolean)).size,
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-14 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 rounded-2xl" />
          ))}
        </div>
        <div className="h-80 bg-gray-200 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-4 animate-fadeIn">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary-50">
            <HiGlobeAlt className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-800">طلبات مندواي</h3>
            <p className="text-xs text-gray-400">الطلبات الواردة من تطبيق مندواي</p>
          </div>
          <button
            onClick={() => fetchOrders(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-50 text-primary-600 text-sm font-semibold hover:bg-primary-100 transition-all active:scale-95 disabled:opacity-50"
          >
            <HiRefresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 animate-fadeIn">
          <HiExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">فشل في تحميل الطلبات</p>
            <p className="text-xs text-red-500 mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => fetchOrders()}
            className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard
          title="عدد الطلبات"
          value={summary.orderCount || 0}
          icon={HiShoppingBag}
          subtitle="طلبات مندواي"
          color="primary"
        />
        <KPICard
          title="إجمالي المبيعات"
          value={`${money(summary.totalRevenue)} د.ا`}
          icon={HiCurrencyDollar}
          subtitle="قيمة الطلبات"
          color="green"
        />
        <KPICard
          title="عدد العناصر"
          value={summary.totalItems || 0}
          icon={HiCube}
          subtitle="القطع المباعة"
          color="blue"
        />
        <KPICard
          title="عدد العملاء"
          value={summary.customerCount || 0}
          icon={HiUsers}
          subtitle="عملاء مندواي"
          color="orange"
        />
      </div>

      {/* Orders */}
      <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 overflow-hidden animate-fadeIn">
        <div className="flex items-center gap-3 p-5 border-b border-gray-100">
          <div className="p-2.5 rounded-xl bg-primary-50">
            <HiShoppingBag className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-800">سجل الطلبات</h3>
            <p className="text-xs text-gray-400">{orders.length} طلب</p>
          </div>
        </div>

        {orders.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {orders.map((order) => (
              <div key={order.id}>
                <button
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  className="w-full text-right hover:bg-primary-50/30 transition-colors"
                >
                  <div className="flex items-center gap-4 px-5 py-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-400 flex items-center justify-center shadow-sm flex-shrink-0">
                      <HiGlobeAlt className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-800">{order.orderNumber}</span>
                        <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md">
                          {(order.Items || []).length} منتج
                        </span>
                        <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                          {order.orderStatusText}
                        </span>
                        <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                          {order.paymentStatusText}
                        </span>
                        {order.paymentMethod && (
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                            {order.paymentMethod}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <HiClock className="w-3 h-3" />
                          {new Date(order.createdAt).toLocaleDateString('ar-EG', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                          {' '}
                          {new Date(order.createdAt).toLocaleTimeString('ar-EG', {
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <HiUser className="w-3 h-3" />
                          {order.users?.fullName || order.customerId || 'بدون عميل'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-emerald-600">{money(order.total)} د.ا</span>
                      <HiChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                        expandedOrder === order.id ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                </button>

                {expandedOrder === order.id && (
                  <div className="bg-gray-50/60 border-t border-gray-100 px-5 py-4 animate-fadeIn">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            <th className="px-3 py-2">المنتج</th>
                            <th className="px-3 py-2">الباركود</th>
                            <th className="px-3 py-2">الكمية</th>
                            <th className="px-3 py-2">سعر الوحدة</th>
                            <th className="px-3 py-2">الإجمالي</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(order.Items || []).map((item, i) => (
                            <tr key={item.itemId || i}>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center">
                                    <span className="text-[10px] font-bold text-primary-600">
                                      {item.productName ? item.productName.charAt(0) : '—'}
                                    </span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-700">{item.productName}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-xs text-gray-400 font-mono bg-white px-1.5 py-0.5 rounded border border-primary-100/40">
                                  {item.barCode || item.sku || '—'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-sm font-bold text-gray-700">{item.quantity}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-sm text-gray-600">{money(item.unitPrice)} د.ا</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-sm font-bold text-emerald-600">{money(item.totalPrice)} د.ا</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-200">
                            <td colSpan="4" className="px-3 py-2.5 text-sm font-bold text-gray-700 text-left">
                              الإجمالي
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-sm font-bold text-primary-600">{money(order.total)} د.ا</span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <HiGlobeAlt className="w-12 h-12 mb-3" />
            <p className="text-base font-semibold text-gray-400">لا توجد طلبات</p>
            <p className="text-sm text-gray-300 mt-1">لم يتم استلام أي طلب من مندواي بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ title, value, icon: Icon, subtitle, color = 'primary' }) {
  const colorClasses = {
    primary: { bg: 'bg-primary-50', text: 'text-primary-600', glow: 'shadow-primary-500/10' },
    green: { bg: 'bg-emerald-50', text: 'text-emerald-600', glow: 'shadow-emerald-500/10' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', glow: 'shadow-blue-500/10' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', glow: 'shadow-orange-500/10' },
  };
  const c = colorClasses[color];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-md hover:-translate-y-1 transition-all duration-300 animate-fadeIn">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 font-medium mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${c.bg} ${c.text} shadow-lg ${c.glow}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}
